import { SimpleEmailMessage } from "@redotech/redo-merchant-app-common/simple-email-message";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import FlipBackwardIcon from "@redotech/redo-web/icon-old/flip-backward.svg";
import ForwardArrowIcon from "@redotech/redo-web/icon-old/forward-arrow.svg";
import { Text } from "@redotech/redo-web/text";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { memo, useState } from "react";

export interface EmailContact {
  name: string | null;
  email: string;
}

export interface FormattedEmailMessage {
  id: string;
  senderName: string;
  senderInitials: string;
  isMessageFromCustomer: boolean;
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  bcc: EmailContact[];
  recipientSummary: string | null;
  content: string;
  htmlContent: string | null;
  timestamp: Date;
  formattedTime: string;
  preview: string;
}

interface EmailMessageCardProps {
  message: FormattedEmailMessage;
  isExpanded: boolean;
  isLast?: boolean;
  onToggleExpand: () => void;
  onReply?: () => void;
  onForward?: () => void;
}

export const EmailMessageCard = memo(function EmailMessageCard({
  message,
  isExpanded,
  isLast,
  onToggleExpand,
  onReply,
  onForward,
}: EmailMessageCardProps) {
  return (
    <Flex
      bgColor="primary"
      borderBottomWidth={isLast ? "1px" : undefined}
      borderColor="primary"
      borderLeftWidth="1px"
      borderRightWidth="1px"
      borderStyle="solid"
      bottomLeftRadius={isLast ? "sm" : undefined}
      bottomRightRadius={isLast ? "sm" : undefined}
      dir="column"
      gap="md"
      p="xl"
    >
      <MessageHeader
        formattedTime={message.formattedTime}
        isExpanded={isExpanded}
        isMessageFromCustomer={message.isMessageFromCustomer}
        onToggleExpand={onToggleExpand}
        preview={message.preview}
        senderInitials={message.senderInitials}
        senderName={message.senderName}
        to={message.to}
      />
      {isExpanded && (
        <>
          <MessageContent
            content={message.content}
            htmlContent={message.htmlContent}
          />
          <MessageActions onForward={onForward} onReply={onReply} />
        </>
      )}
    </Flex>
  );
});

const MessageHeader = memo(function MessageHeader({
  senderName,
  senderInitials,
  isMessageFromCustomer,
  to,
  formattedTime,
  preview,
  isExpanded,
  onToggleExpand,
}: {
  senderName: string;
  senderInitials: string;
  isMessageFromCustomer: boolean;
  to: { name: string | null; email: string }[];
  formattedTime: string;
  preview: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const [mouseDownLoc, setMouseDownLoc] = useState<
    { x: number; y: number } | undefined
  >();

  return (
    <Flex
      align="flex-start"
      cursor="pointer"
      gap="md"
      justify="space-between"
      onMouseDown={(e) => {
        setMouseDownLoc({ x: e.clientX, y: e.clientY });
      }}
      onMouseUp={(e) => {
        if (
          mouseDownLoc &&
          Math.abs(e.clientX - mouseDownLoc.x) < 5 &&
          Math.abs(e.clientY - mouseDownLoc.y) < 5
        ) {
          onToggleExpand();
        }
      }}
    >
      <Flex align="flex-start" gap="md" grow={1} style={{ minWidth: 0 }}>
        <SenderAvatar senderName={senderName} />
        <Flex dir="column" gap="none" grow={1} style={{ minWidth: 0 }}>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {senderName}
          </Text>
          {!isExpanded && (
            <Text
              fontSize="sm"
              style={{ minWidth: 0 }}
              textColor="tertiary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {preview}
            </Text>
          )}
          {isExpanded && to.length > 0 && (
            <Flex align="center" gap="xs">
              <Text fontSize="xs" textColor="tertiary">
                to:
              </Text>
              <div onMouseUp={(e) => e.stopPropagation()}>
                <CopyableText fontSize="xs" textColor="tertiary">
                  {to.map((addr) => addr.email).join(", ")}
                </CopyableText>
              </div>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex shrink={0}>
        <Text fontSize="xs" textColor="tertiary">
          {formattedTime}
        </Text>
      </Flex>
    </Flex>
  );
});

const SenderAvatar = memo(function SenderAvatar({
  senderName,
}: {
  senderName: string;
}) {
  return (
    <UserImage
      alt={`${senderName} avatar`}
      imageUrl={null}
      name={senderName}
      size={UserImageSize.SMALL}
    />
  );
});

const MessageContent = memo(function MessageContent({
  content,
  htmlContent,
}: {
  content: string;
  htmlContent: string | null;
}) {
  return (
    <Flex dir="column" ml="5xl">
      <SimpleEmailMessage
        messageContent={
          htmlContent
            ? { type: "messageHtml", messageHtml: htmlContent }
            : { type: "messageText", messageText: content || "(No content)" }
        }
      />
    </Flex>
  );
});

const MessageActions = memo(function MessageActions({
  onReply,
  onForward,
}: {
  onReply?: () => void;
  onForward?: () => void;
}) {
  return (
    <Flex gap="md" ml="5xl" pt="md">
      <RedoButton
        hierarchy="secondary"
        IconLeading={FlipBackwardIcon}
        onClick={onReply}
        size="sm"
        text="Reply"
      />
      <RedoButton
        hierarchy="secondary"
        IconLeading={ForwardArrowIcon}
        onClick={onForward}
        size="sm"
        text="Forward"
      />
    </Flex>
  );
});
